import { Card, Empty, Flex, List, Pagination, Space } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { DeleteLocalDraftButton } from '@/components/DeleteLocalDraftButton';
import {
  MeterRecordListRow,
  MeterRecordSourceTag,
  MeterRecordStatusTag,
  readMeterListItemSummary,
} from '@/components/MeterRecordSummary';
import {
  METER_LIST_ITEM_KINDS,
  type MeterListItem,
  openMeterListItem,
} from '@/features/meters/meter-record-list-model';
import { isDeletableLocalSession, isSubmittedSession } from '@/shared/capture/capture-session';
import type { MeterSearchResult } from '@/shared/meter-identification';
import { messages } from './messages';

interface MeterRecordsListProps {
  deletingSessionId: string | null;
  isLoading: boolean;
  isOpeningMeter: boolean;
  items: MeterListItem[];
  onDeleteLocalDraft: (sessionId: string) => Promise<void> | void;
  onOpenMeter: (meter: MeterSearchResult) => Promise<void> | void;
  onOpenQueuedMeter: (sessionId: string) => void;
}

const PAGE_SIZE = 10;

export function MeterRecordsList({
  deletingSessionId,
  isLoading,
  isOpeningMeter,
  items,
  onDeleteLocalDraft,
  onOpenMeter,
  onOpenQueuedMeter,
}: MeterRecordsListProps) {
  const intl = useIntl();
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const visibleItems = useMemo(() => {
    const offset = (safePage - 1) * PAGE_SIZE;

    return items.slice(offset, offset + PAGE_SIZE);
  }, [items, safePage]);

  useEffect(() => {
    setPage(current => Math.min(current, pageCount));
  }, [pageCount]);

  return (
    <Card size="small" title={intl.formatMessage(messages.metersRecordsTitle)}>
      {items.length === 0 ? (
        <Empty
          description={
            isLoading
              ? intl.formatMessage(messages.metersLoadingCachedMeters)
              : intl.formatMessage(messages.metersNoMetersMatchSearch)
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <List
            dataSource={visibleItems}
            loading={isLoading || isOpeningMeter}
            rowKey={item => item.id}
            renderItem={item => (
              <MeterListRecordRow
                detailsFallback={intl.formatMessage(messages.metersNoPlacementDetails)}
                deletingSessionId={deletingSessionId}
                item={item}
                onDeleteLocalDraft={onDeleteLocalDraft}
                onOpenMeter={onOpenMeter}
                onOpenQueuedMeter={onOpenQueuedMeter}
              />
            )}
          />
          {items.length > PAGE_SIZE ? (
            <Flex justify="center">
              <Pagination
                current={safePage}
                onChange={setPage}
                pageSize={PAGE_SIZE}
                showSizeChanger={false}
                size="small"
                total={items.length}
              />
            </Flex>
          ) : null}
        </Space>
      )}
    </Card>
  );
}

function MeterListRecordRow({
  deletingSessionId,
  detailsFallback,
  item,
  onDeleteLocalDraft,
  onOpenMeter,
  onOpenQueuedMeter,
}: {
  deletingSessionId: string | null;
  detailsFallback: string;
  item: MeterListItem;
  onDeleteLocalDraft: (sessionId: string) => Promise<void> | void;
  onOpenMeter: (meter: MeterSearchResult) => Promise<void> | void;
  onOpenQueuedMeter: (sessionId: string) => void;
}) {
  const intl = useIntl();
  const summary = readMeterListItemSummary(item, intl);
  const deletableSession =
    item.kind === METER_LIST_ITEM_KINDS.session && isDeletableLocalSession(item.session) ? item.session : null;

  return (
    <MeterRecordListRow
      actions={
        deletableSession
          ? [
              <DeleteLocalDraftButton
                isDeleting={deletingSessionId === deletableSession.id}
                isSubmitted={isSubmittedSession(deletableSession)}
                key="delete"
                onDelete={() => void onDeleteLocalDraft(deletableSession.id)}
              />,
            ]
          : undefined
      }
      detailsFallback={detailsFallback}
      onOpen={() => openMeterListItem(item, onOpenQueuedMeter, onOpenMeter)}
      rightExtra={<MeterRecordStatusTag status={readMeterRowStatus(item)} />}
      summary={summary}
      titlePrefix={<MeterRecordSourceTag dbId={summary.dbId} />}
    />
  );
}

function readMeterRowStatus(item: MeterListItem) {
  return item.kind === METER_LIST_ITEM_KINDS.session ? item.session.status : (item.meter.data.status ?? 'cached');
}
